import { FlexColumn, LabeledField, LabeledInput, useLoading } from '@gorila-shared-ui/components';
import { Checkbox } from 'baseui/checkbox';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES, REQUIRED_FIELD_TEXT } from '../../constants/app';
import { useFeedback } from '../../hooks/useFeedback';
import { useForms } from '../../hooks/useForms';
import { useStyles } from '../../hooks/useStyles';
import { createTenant, createTenantImage, updateTenant } from '../../services/tenantsService';
import {
  editTenantErrorState,
  editTenantState,
  editTenantTouchedState,
  editTenantValidState,
} from '../../storage/tenantsStates';
import { Tenant, TenantRequest, TenantRequestTouched } from '../../types/tenants';
import { FormActionsRow } from '../shared/FormActionsRow';
import { ImageUploader } from '../shared/ImageUploader';
import StyledModal from '../shared/StyledModal';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  tenantId?: string;
  tenant?: Tenant;
  onSubmitFinished?: () => void;
};

export function TenantModal({ isOpen, onClose, tenantId, tenant, onSubmitFinished }: Readonly<Props>) {
  const editMode = tenantId !== undefined && tenant !== undefined;
  const { css } = useStyles();
  const [form, setForm] = useRecoilState(editTenantState);
  const [formTouched, setFormTouched] = useRecoilState(editTenantTouchedState);
  const formHasError = useRecoilValue(editTenantErrorState);
  const resetEditTenantState = useResetRecoilState(editTenantState);
  const resetEditTenantTouchedState = useResetRecoilState(editTenantTouchedState);
  const formValid = useRecoilValue(editTenantValidState);
  const { loading, startLoading, stopLoading } = useLoading();
  const canSubmit = !formHasError;
  const { showFailFeedback, showPositiveFeedback } = useFeedback();
  const { onTouched } = useForms<TenantRequest, TenantRequestTouched>(setForm, setFormTouched);
  const [file, setFile] = useState<File>();

  useEffect(() => {
    if (isOpen) {
      return clearStates();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!tenant) return;
    setForm(tenant);
  }, [tenant]);

  const clearStates = () => {
    resetEditTenantState();
    resetEditTenantTouchedState();
    setFile(undefined);
  };

  const onChangeInput = (key: keyof TenantRequest, value: any) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const onSubmit = async () => {
    const onSuccess = (successMessage: string) => {
      showPositiveFeedback(successMessage);
      clearStates();
      onSubmitFinished && onSubmitFinished();
    };
    startLoading();
    if (editMode) {
      let logo = '';
      if (file) {
        const { path, error } = await createTenantImage(tenantId, file);
        if (!error && path) {
          logo = path;
        } else if (error) {
          showFailFeedback(error);
          stopLoading();
          return;
        }
      }
      const { error } = await updateTenant(tenantId, { ...form, logo });
      if (!error) {
        onSuccess(FEEDBACK.edited(FEEDBACK_PREFIXES.tenant, form.name));
      } else {
        showFailFeedback(error);
      }
    } else {
      const { error } = await createTenant(form);
      if (!error) {
        onSuccess(FEEDBACK.created(FEEDBACK_PREFIXES.tenant));
      } else {
        showFailFeedback(error);
      }
    }
    stopLoading();
  };

  const onCloseModal = () => {
    clearStates();
    onClose();
  };

  return (
    <StyledModal
      isOpen={isOpen}
      closeable
      onClose={onCloseModal}
      size="auto"
      title={editMode ? 'Editar ' : 'Nuevo '}
      content={
        <FlexColumn
          classNames={css({
            justifyContent: 'space-between',
            overflow: 'hidden',
            minWidth: '525px',
          })}
        >
          <FlexColumn
            classNames={css({
              overflow: 'auto',
              height: '100%',
            })}
          >
            <LabeledInput
              label="Nombre"
              placeholder="Ingresa nombre"
              value={form.name}
              onChange={(value) => {
                onChangeInput('name', value);
              }}
              onBlur={() => onTouched('nameTouched')}
              error={formTouched.nameTouched && !formValid.name && REQUIRED_FIELD_TEXT}
              required
            />
            <LabeledInput
              label="Razón social"
              placeholder="Ingresa la razón social"
              value={form.socialReason}
              onChange={(value) => {
                onChangeInput('socialReason', value);
              }}
            />
            {editMode && (
              <LabeledField label="Logo">
                <ImageUploader
                  imageS3Url={form.logo}
                  onFileChange={(file) => {
                    setFile(file);
                    onChangeInput('logo', '');
                  }}
                  file={file}
                />
              </LabeledField>
            )}
            {editMode && (
              <Checkbox
                checked={form.active}
                onChange={(e) => onChangeInput('active', e.target.checked)}
              >
                Activo
              </Checkbox>
            )}
          </FlexColumn>
          <FormActionsRow
            canSubmit={canSubmit}
            loading={loading}
            editMode={editMode}
            onSubmit={onSubmit}
            onCancel={onCloseModal}
          />
        </FlexColumn>
      }
    />
  );
}
